import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Switch,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import { darken, lighten } from '@mui/material/styles'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'

import { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { useBlocker, useSearchParams } from 'react-router-dom'
import {
  ExpandMore as ExpandMoreIcon,
  InfoOutlined,
} from '@mui/icons-material'
import { UUID } from 'crypto'
import type { FundingNeedDraft } from '@/models/FundingNeeds'
import { FundingNeedResponse } from '@/models/FundingNeeds'
import { useFundingNeeds } from '@/utils/hooks/fundingNeeds'
import { useScanIdsByNeedId } from '@/utils/hooks/scans'
import FundingNeedsForm, { FundingNeedsFormRef } from './FundingNeedsForm'
import Loading from '../Loading'
import UploadFundingNeedFile from '../UploadFundingNeedFile'
import UploadFundingNeedWebsiteUrls from '../UploadFundingNeedWebsiteUrl'
import FundingNeedDocList from '../FundingNeedDocList'
import { useRegions } from '@/utils/hooks/regions'
import { RegionData } from '@/models/Regions'
import { useOnboardingStatus } from '@/pages/onboarding/hooks/useOnboardingStatus'
import { AccessibleTooltip } from '../AccessibleTooltip'
import GenerateSponsorshipLetter from '../GenerateSponsorshipLetter'
import { usePausedOrg } from '@/utils/hooks/usePausedOrg'
import { useStagedContentGuard } from '../StagedContentGuardProvider'
import { ScanIdsData } from '@/models/Scans'
import FundingNeedMatchActions from './FundingNeedMatchActions'
import FundingNeedStaleWarningIcon from './FundingNeedStaleWarningIcon'
import OrgMatchPreferences from '../OrgMatchPreferences'

interface FundingNeedsListProps {
  orgUuid: UUID
  token: string
  org_accepts_gambling_proceeds: boolean
  org_accepts_international: boolean
  org_need_only_context?: boolean
  org_sponsorship_letter?: boolean
  org_can_set_preferences?: boolean
  org_region_ids: number[]
  isAnnualSubscriber?: boolean
  prefillDraft?: FundingNeedDraft | null
  setPrefillDraft?: (draft: FundingNeedDraft | null) => void
  onPrefillConsumed?: () => void
  orgWebsite?: string | null
}

const STAGED_UPLOAD_COLLAPSE_CONFIRM =
  "You have a file or website that hasn't been uploaded yet. Are you sure you want to close this view?"

const STAGED_UPLOAD_EDIT_CONFIRM =
  "You have a file or website that hasn't been uploaded yet. Are you sure you want to edit this funding request? Any files or websites you have selected to upload will be lost."

const STAGED_UPLOAD_NAV_CONFIRM =
  "You have a file or website that hasn't been uploaded yet. Are you sure you want to leave this page? Any files or websites you have selected to upload will be lost."

const STAGED_UPLOAD_ARCHIVE_CONFIRM =
  "You have a file or website that hasn't been uploaded yet. Are you sure you want to archive this funding request? Any files or websites you have selected to upload will be lost."

// Unlike STAGED_UPLOAD_COLLAPSE_CONFIRM, these two transitions genuinely
// unmount the row (the add-form replaces the whole list; the Show Archived
// filter drops the row from it) rather than just visually hiding it, so the
// "close this view" wording would be misleading here - it has to say the
// content is actually lost, the same way Edit/Archive/nav already do.
const STAGED_UPLOAD_DISCARD_CONFIRM =
  "You have a file or website that hasn't been uploaded yet. Are you sure you want to continue? Any files or websites you have selected to upload will be lost."

// Shared by both "Upload Files" / "Upload Website URLs" toggle CTAs below.
const uploadTypeToggleButtonSx = {
  mt: 2,
  borderColor: '#1C3045',
  bgcolor: '#fff',
  color: '#1C3045',
  textTransform: 'none',
  fontSize: '16px',
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
  '&:focus': {
    border: '2px solid #1C3045',
  },
} as const

export default function FundingNeedsList({
  orgUuid,
  token,
  org_accepts_gambling_proceeds,
  org_accepts_international,
  org_need_only_context,
  org_sponsorship_letter,
  org_can_set_preferences,
  org_region_ids,
  isAnnualSubscriber = false,
  prefillDraft,
  setPrefillDraft,
  onPrefillConsumed,
  orgWebsite,
}: FundingNeedsListProps) {
  const [showArchived, setShowArchived] = useState(false)
  const [showAddForm, setShowAddForm] = useState(false)
  const [activePrefill, setActivePrefill] = useState<FundingNeedDraft | null>(
    null,
  )
  const [editingNeedId, setEditingNeedId] = useState<number | null>(null)
  // Read by an edit form's onSuccess, which can fire after the user has
  // already moved on to editing a different row (the save was still running
  // when they switched) - it must not close or steal focus from that row.
  const editingNeedIdRef = useRef(editingNeedId)
  editingNeedIdRef.current = editingNeedId
  // Tracks the row whose edit was just saved, so a brief confirmation can be
  // announced - saving no longer navigates away, so nothing else does this
  // for us. `seq` changes on every save, so saving the same row twice still
  // re-runs the effect below instead of being swallowed as an unchanged value.
  const [justSaved, setJustSaved] = useState<{
    needId: number
    seq: number
  } | null>(null)
  const saveSeqRef = useRef(0)
  const editButtonRefs = useRef<Record<number, HTMLButtonElement | null>>({})
  // The row whose Edit button should take focus once its form has closed,
  // by Save or by the form's own Cancel. The form's buttons unmount with it,
  // which otherwise drops focus to <body>. `seq` works as it does for
  // justSaved.
  const [returnFocusTo, setReturnFocusTo] = useState<{
    needId: number
    seq: number
  } | null>(null)
  const focusSeqRef = useRef(0)
  const returnFocusToEdit = (needId: number) => {
    focusSeqRef.current += 1
    setReturnFocusTo({ needId, seq: focusSeqRef.current })
  }
  // Keyed by need.id, not one shared value - every row's quick-upload widget
  // stays mounted at once (MUI doesn't unmount collapsed AccordionDetails),
  // so a shared upload-type selector let switching tabs on one row unmount
  // every OTHER row's widget too, destroying their staged content (#1465).
  // uploadTabValue is derived rather than tracked separately, since it's
  // fully determined by showUploadType.
  const [showUploadTypeByNeedId, setShowUploadTypeByNeedId] = useState<
    Record<number, 'files' | 'websites' | undefined>
  >({})
  // Mirrors the same guard in FundingNeedsForm.tsx (#1465): this quick-upload
  // widget (shown on an expanded, non-editing row) has its own separate
  // "Upload" button, so staged-but-not-uploaded content here must also block
  // switching tabs or collapsing the accordion, not just Save in the form.
  // Keyed by need.id rather than one shared flag - every row's widget stays
  // mounted at once (MUI doesn't unmount collapsed AccordionDetails), so a
  // shared boolean let one row's Edit/collapse clobber another row's still-
  // staged content.
  const [pendingFilesByNeedId, setPendingFilesByNeedId] = useState<
    Record<number, boolean>
  >({})
  const [pendingWebsitesByNeedId, setPendingWebsitesByNeedId] = useState<
    Record<number, boolean>
  >({})
  const anyRowHasPendingUpload =
    Object.values(pendingFilesByNeedId).some(Boolean) ||
    Object.values(pendingWebsitesByNeedId).some(Boolean)
  // Reported up by whichever FundingNeedsForm is nested here (add or edit
  // mode) - a router only honours one active useBlocker, so this list owns
  // the single instance and folds the nested form's own pending state into
  // it rather than each keeping a separate, competing blocker.
  const [formPendingUpload, setFormPendingUpload] = useState(false)
  const handlePendingUploadChange = useCallback((pending: boolean) => {
    setFormPendingUpload(pending)
  }, [])
  const hasPendingUpload = anyRowHasPendingUpload || formPendingUpload
  // Read from the prefillDraft effect below without depending on it there -
  // that effect should only re-check pending state when prefillDraft itself
  // changes (a genuine attempt to switch to add-mode), not every time some
  // row's pending state happens to change while add-mode is already open.
  const hasPendingUploadRef = useRef(hasPendingUpload)
  hasPendingUploadRef.current = hasPendingUpload

  // Same-page transitions (tab-switch, accordion-collapse) are guarded
  // inline above; this reuses FundingNeedsForm's pattern (itself reused from
  // pages/ProfilePage.tsx) for the two exits those guards can't reach:
  // navigating elsewhere in the app, and refreshing/closing/back-navigating
  // the browser itself.
  useBlocker(() => {
    if (hasPendingUpload) {
      return !window.confirm(STAGED_UPLOAD_NAV_CONFIRM)
    }
    return false
  })

  useEffect(() => {
    if (!hasPendingUpload) return
    const handler = (e: BeforeUnloadEvent) => e.preventDefault()
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [hasPendingUpload])

  const [expandedAccordion, setExpandedAccordion] = useState<number | null>(
    null,
  )
  const [showArchiveWarning, setShowArchiveWarning] = useState<number | null>(
    null,
  )
  const [cancelConfirmation, setCancelConfirmation] = useState<{
    type: 'edit' | null
    needId?: number
    // Set when this confirmation is standing in for switching to a
    // different need's edit form, rather than a plain Cancel click -
    // accepting opens that need instead of just closing.
    switchToNeedId?: number
    // True when the need being switched TO also has staged quick-upload
    // content of its own, so the dialog can name both losses at once.
    rowHasStagedUpload?: boolean
  }>({ type: null })
  const [formChanges, setFormChanges] = useState<{
    edit: boolean
  }>({ edit: false })

  // The org selector lives outside this page, in the shared nav - it can't
  // see this state directly, so report it up for the org-switch guard.
  // Includes unsaved field edits, not just staged uploads: the shared confirm
  // promises "unsaved changes, or a file or website that hasn't been uploaded
  // yet", and ProfilePage reports its own unsaved edits too. Declared here
  // rather than beside hasPendingUpload because formChanges is set below.
  const { reportStagedContent } = useStagedContentGuard()
  const hasUnsavedOrStagedContent = hasPendingUpload || formChanges.edit
  useEffect(() => {
    reportStagedContent('fundingNeeds', hasUnsavedOrStagedContent)
    return () => reportStagedContent('fundingNeeds', false)
  }, [hasUnsavedOrStagedContent, reportStagedContent])
  const [showSponsorshipModal, setShowSponsorshipModal] = useState(false)
  const [sponsorshipNeed, setSponsorshipNeed] =
    useState<FundingNeedResponse | null>(null)
  const [preferencesNeedId, setPreferencesNeedId] = useState<number | null>(
    null,
  )
  const [searchParams, setSearchParams] = useSearchParams()

  const addFormRef = useRef<FundingNeedsFormRef>(null)

  const {
    data: fundingNeeds,
    isLoading,
    error,
    updateFundingNeedArchiveStatus,
  } = useFundingNeeds(token, orgUuid)

  const { data: scanIds } = useScanIdsByNeedId(
    token as string,
    (fundingNeeds && fundingNeeds?.map((fundingNeed) => fundingNeed.id)) || [],
  ) as { data: ScanIdsData[]; isLoading: boolean; isError: boolean }

  const {
    regions,
    isLoading: regionsLoading,
    isError: regionsError,
  } = useRegions(token as string)

  const { isOnboarding } = useOnboardingStatus()
  const { canWrite } = usePausedOrg()

  // Also read without depending on it - onPrefillConsumed is a fresh inline
  // callback each render at every call site today, so it's currently
  // harmless in the dependency array, but the add form is a likely future
  // home for the same upload widgets, and this effect must not start
  // re-firing its confirm every render once that callback stops being
  // memoized.
  const onPrefillConsumedRef = useRef(onPrefillConsumed)
  onPrefillConsumedRef.current = onPrefillConsumed
  useEffect(() => {
    if (!prefillDraft) {
      setActivePrefill(null)
      setShowAddForm(false)
      return
    }
    // "Add a Funding Request" and selecting a generated draft both arrive
    // here as a new prefillDraft, and both swap the whole row list out for
    // the add-form below - unmounting any row's still-staged quick-upload
    // content with no warning at all otherwise (#1465).
    if (
      hasPendingUploadRef.current &&
      !window.confirm(STAGED_UPLOAD_DISCARD_CONFIRM)
    ) {
      onPrefillConsumedRef.current?.()
      return
    }
    setActivePrefill(prefillDraft)
    setShowAddForm(true)
  }, [prefillDraft])

  useEffect(() => {
    if (!fundingNeeds) return

    const editNeedParam = searchParams.get('editNeed')
    if (!editNeedParam) return

    const editNeedId = Number(editNeedParam)
    const targetNeed = fundingNeeds.find((need) => need.id === editNeedId)

    if (targetNeed) {
      if (targetNeed.is_archived) {
        setShowArchived(true)
      }
      setExpandedAccordion(editNeedId)
      if (canWrite) {
        setEditingNeedId(editNeedId)
      }
    }

    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete('editNeed')
    setSearchParams(nextParams, { replace: true })
  }, [canWrite, fundingNeeds, searchParams, setSearchParams])

  // Saving an edit collapses the form back into this row rather than
  // navigating away, so nothing else announces success - clear the
  // confirmation after a few seconds so it doesn't linger indefinitely.
  useEffect(() => {
    if (justSaved === null) return
    const timer = setTimeout(() => setJustSaved(null), 5000)
    return () => clearTimeout(timer)
  }, [justSaved])

  useEffect(() => {
    if (returnFocusTo === null) return
    editButtonRefs.current[returnFocusTo.needId]?.focus()
  }, [returnFocusTo])

  // lookup maps for iwi and regions
  const regionMap = useMemo(() => {
    if (!Array.isArray(regions)) return new Map()
    return new Map(regions.map((region: RegionData) => [region.id, region]))
  }, [regions])

  const handleAddFormClose = () => {
    setShowAddForm(false)
    if (activePrefill) {
      setActivePrefill(null)
      onPrefillConsumed?.()
    }
  }

  const handleAddSuccess = () => {
    handleAddFormClose()
  }

  function handleCancel() {
    // use the form's own cancel logic via ref
    addFormRef.current?.handleCancel()
  }

  // Only one row can be expanded at a time, so expanding a different row
  // implicitly collapses whichever was open - that needs the same
  // staged-upload confirm as explicitly clicking to collapse it, since
  // otherwise switching rows silently hides a still-staged upload with no
  // warning at all (#1465).
  function handleAccordionToggle(needId: number) {
    if (expandedAccordion === needId) {
      if (
        (pendingFilesByNeedId[needId] || pendingWebsitesByNeedId[needId]) &&
        !window.confirm(STAGED_UPLOAD_COLLAPSE_CONFIRM)
      ) {
        return
      }
      setExpandedAccordion(null)
      return
    }
    if (
      expandedAccordion !== null &&
      (pendingFilesByNeedId[expandedAccordion] ||
        pendingWebsitesByNeedId[expandedAccordion]) &&
      !window.confirm(STAGED_UPLOAD_COLLAPSE_CONFIRM)
    ) {
      return
    }
    setExpandedAccordion(needId)
  }

  // Turning "Show Archived" off removes archived rows from the filtered
  // list below - the same silent-unmount risk as switching rows, just via
  // the filter instead of the accordion (#1465). Covers both the quick-
  // upload widgets (keyed maps) and the real edit form nested in an
  // archived row (formPendingUpload) - the form's own state isn't keyed by
  // need.id, so it only counts if the row currently being edited is itself
  // archived.
  function handleShowArchivedToggle(nextChecked: boolean) {
    if (!nextChecked) {
      const editingArchivedNeedPending =
        formPendingUpload &&
        editingNeedId !== null &&
        fundingNeeds?.some(
          (need) => need.id === editingNeedId && need.is_archived,
        )
      const hidingPendingArchived =
        editingArchivedNeedPending ||
        fundingNeeds?.some(
          (need) =>
            need.is_archived &&
            (pendingFilesByNeedId[need.id] || pendingWebsitesByNeedId[need.id]),
        )
      if (
        hidingPendingArchived &&
        !window.confirm(STAGED_UPLOAD_DISCARD_CONFIRM)
      ) {
        return
      }
    }
    setShowArchived(nextChecked)
  }

  // Memoize the onFormStateChange callback to prevent infinite re-renders
  const handleFormStateChange = useCallback((hasChanges: boolean) => {
    setFormChanges({ edit: hasChanges })
  }, [])

  const filteredNeeds = fundingNeeds?.filter(
    (need) => showArchived || !need.is_archived,
  )

  function handleShowUploadTypeToggle(
    needId: number,
    uploadType: 'files' | 'websites',
  ) {
    setShowUploadTypeByNeedId((prev) => ({
      ...prev,
      [needId]: prev[needId] === uploadType ? undefined : uploadType,
    }))
  }

  function handleUploadTabChange(needId: number, newValue: number) {
    setShowUploadTypeByNeedId((prev) => ({
      ...prev,
      [needId]: newValue === 0 ? 'files' : 'websites',
    }))
  }

  if (isLoading || regionsLoading) {
    return <Loading />
  }

  if (error || regionsError) {
    return <Typography color="error">Error loading funding requests</Typography>
  }

  return (
    <Box
      sx={{
        margin: '0 auto',
        marginTop: 2,
        width: '100%',
        pb: 5,
      }}
    >
      {showArchiveWarning && (
        <Dialog
          open={showArchiveWarning !== null}
          onClose={() => setShowArchiveWarning(null)}
          aria-labelledby="warning-dialog-title"
          aria-describedby="warning-dialog-description"
        >
          <DialogTitle id="warning-dialog-title">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="h6" sx={{ mt: 1, textAlign: 'center' }}>
                Are you sure you want to archive this funding request?
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <DialogContentText
              id="warning-dialog-description"
              sx={{ color: '#1C3045', mt: 1 }}
            >
              <Typography variant="body1" sx={{ width: '90%' }}>
                Archiving this funding need will hide it from view on all pages
                - Funding Requests, Match Results, and Applications.
              </Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>
                If you need to access it again, you can toggle the "Show
                Archived" option at the top of these pages.
              </Typography>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>
                Archiving isn't permanant - You can unarchive funding requests
                at any time.
              </Typography>
            </DialogContentText>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => setShowArchiveWarning(null)}
              variant="outlined"
              disableRipple
              sx={{
                color: '#1C3045',
                borderColor: '#1C3045',
                textTransform: 'none',
                fontSize: '16px',
                width: '150px',
                '&:focus': {
                  backgroundColor: '#e8e8e8',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                updateFundingNeedArchiveStatus.mutate({
                  fundingNeedId: showArchiveWarning, // the funding need ID
                  token,
                })
                setShowArchiveWarning(null)
              }}
              variant="contained"
              disableRipple
              sx={{
                color: 'white',
                backgroundColor: '#1C3045',
                textTransform: 'none',
                fontSize: '16px',
                width: '150px',
              }}
            >
              Archive
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Cancel Confirmation Dialog */}
      {cancelConfirmation.type === 'edit' && (
        <Dialog
          open={cancelConfirmation.type === 'edit'}
          onClose={() => setCancelConfirmation({ type: null })}
          aria-labelledby="cancel-dialog-title"
          aria-describedby="cancel-dialog-description"
        >
          <DialogTitle id="cancel-dialog-title">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="h6" sx={{ mt: 1, textAlign: 'center' }}>
                {cancelConfirmation.switchToNeedId !== undefined
                  ? 'Switch to a different funding request?'
                  : 'Are you sure you want to cancel editing?'}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <DialogContentText
              id="cancel-dialog-description"
              sx={{ color: '#1C3045', mt: 1 }}
            >
              {cancelConfirmation.switchToNeedId !== undefined ? (
                <>
                  <Typography variant="body1" sx={{ width: '90%' }}>
                    You have unsaved changes that will be lost if you switch.
                  </Typography>
                  {cancelConfirmation.rowHasStagedUpload && (
                    <Typography variant="body1" sx={{ mt: 1, width: '90%' }}>
                      The funding request you're switching to also has a file
                      or website that hasn't been uploaded yet — that will be
                      lost too.
                    </Typography>
                  )}
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    Are you sure you want to discard your changes and edit a
                    different funding request instead?
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body1" sx={{ width: '90%' }}>
                    You have unsaved changes that will be lost if you cancel.
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    Are you sure you want to discard your changes and stop
                    editing?
                  </Typography>
                </>
              )}
            </DialogContentText>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => setCancelConfirmation({ type: null })}
              variant="outlined"
              disableRipple
              sx={{
                color: '#1C3045',
                borderColor: '#1C3045',
                textTransform: 'none',
                fontSize: '16px',
                width: '150px',
                '&:focus': {
                  backgroundColor: '#e8e8e8',
                },
              }}
            >
              Keep Editing
            </Button>
            <Button
              onClick={() => {
                const { switchToNeedId, needId } = cancelConfirmation
                setCancelConfirmation({ type: null })
                if (cancelConfirmation.type === 'edit') {
                  setEditingNeedId(switchToNeedId ?? null)
                  setFormChanges({ edit: false })
                  // A plain cancel returns focus to this row's Edit button,
                  // so keep the row open even if another was expanded.
                  if (switchToNeedId === undefined && needId !== undefined) {
                    setExpandedAccordion(needId)
                  }
                }
              }}
              variant="contained"
              disableRipple
              sx={{
                color: 'white',
                backgroundColor: '#1C3045',
                textTransform: 'none',
                fontSize: '16px',
                width: '170px',
              }}
            >
              Discard Changes
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Match Preferences Dialog */}
      <Dialog
        open={preferencesNeedId !== null}
        onClose={() => setPreferencesNeedId(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Match Preferences for: <br />
          {fundingNeeds?.find((n) => n.id === preferencesNeedId)?.title}
        </DialogTitle>
        <DialogContent>
          {preferencesNeedId !== null && (
            <OrgMatchPreferences
              orgUuid={orgUuid as string}
              token={token}
              needId={preferencesNeedId}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPreferencesNeedId(null)}
            disableRipple
            sx={{
              color: '#1C3045',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'rgba(28, 48, 69, 0.04)',
              },
              '&:focus': {
                backgroundColor: '#e8e8e8',
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          mb: 4,
        }}
      >
        {!showAddForm && !isOnboarding && (
          <FormControlLabel
            control={
              <Switch
                checked={showArchived}
                onChange={(e) => handleShowArchivedToggle(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#1C3045',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#1C3045',
                  },
                  '& .MuiSwitch-thumb': {
                    border: showArchived
                      ? '1px solid #1C3045'
                      : '1px solid #666666',
                  },
                }}
              />
            }
            label="Show Archived"
          />
        )}
      </Box>

      {showAddForm ? (
        <>
          <Button
            variant="text"
            disableRipple
            onClick={handleCancel}
            sx={{
              textTransform: 'none',
              color: '#1C3045',
              ':hover': { textDecoration: 'underline' },
              mt: -6,
              mb: 2,
              '&:focus': {
                backgroundColor: '#e8e8e8',
              },
            }}
          >
            <ArrowBackIcon sx={{ mr: 1 }} /> Cancel and go back
          </Button>
          <FundingNeedsForm
            ref={addFormRef}
            orgUuid={orgUuid}
            token={token}
            mode="add"
            onSuccess={handleAddSuccess}
            onCancel={handleAddFormClose}
            prefillDraft={activePrefill}
            setPrefillDraft={setPrefillDraft}
            org_accepts_gambling_proceeds={org_accepts_gambling_proceeds}
            org_accepts_international={org_accepts_international}
            org_need_only_context={org_need_only_context}
            org_region_ids={org_region_ids}
            orgWebsite={orgWebsite}
            onPendingUploadChange={handlePendingUploadChange}
          />
        </>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {filteredNeeds
            ?.sort((a, b) => b.id - a.id)
            .map((need: FundingNeedResponse) => {
              // Keyed by need.id, not shared list-scoped state: MUI's
              // AccordionDetails doesn't unmount collapsed rows by default,
              // so every row's quick-upload widget stays mounted at once -
              // a single shared flag would let one row's Edit/collapse
              // clobber another row's still-staged content (#1465).
              const rowHasPendingFiles = !!pendingFilesByNeedId[need.id]
              const rowHasPendingWebsites = !!pendingWebsitesByNeedId[need.id]
              const rowHasPendingUpload =
                rowHasPendingFiles || rowHasPendingWebsites
              const showUploadType = showUploadTypeByNeedId[need.id]
              const uploadTabValue = showUploadType === 'websites' ? 1 : 0
              return (
              <Accordion
                key={need.id}
                expanded={
                  editingNeedId === need.id || expandedAccordion === need.id
                }
                onChange={() => {
                  if (editingNeedId !== need.id) {
                    handleAccordionToggle(need.id)
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  // Without this the summary button derives its name from every
                  // descendant, so it announces as the title plus each row
                  // action ("... View / Edit View Results Re-find Matches").
                  aria-label={need.title}
                  sx={{
                    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                    '&.Mui-expanded': {
                      borderBottom: 'none',
                    },
                    '& .MuiAccordionSummary-content': {
                      overflow: 'visible',
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      width: '100%',
                      alignItems: 'center',
                      gap: 2,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        minWidth: 0,
                      }}
                    >
                      <Typography>{need.title}</Typography>
                      {need.is_archived && (
                        <Chip
                          label="Archived"
                          size="small"
                          sx={{
                            height: '20px',
                            fontSize: '11px',
                            bgcolor: 'grey.200',
                            color: 'grey.700',
                          }}
                        />
                      )}
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                        justifyContent: 'flex-end',
                      }}
                      onClick={(event) => event.stopPropagation()}
                      onFocus={(event) => event.stopPropagation()}
                    >
                      {/* Fixed width, reserved on every row, so View/Edit
                          and the buttons after it stay aligned whether or
                          not this row has a warning. */}
                      <Box
                        sx={{
                          width: '22px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <FundingNeedStaleWarningIcon
                          token={token}
                          fundingNeed={need}
                          scanIds={
                            scanIds?.find(
                              (item) => item && item.fundingNeedId === need.id,
                            )?.scanIds
                          }
                        />
                      </Box>
                      <Button
                        disableRipple
                        component="span"
                        role="button"
                        tabIndex={0}
                        onClick={(event) => {
                          event.stopPropagation()
                          if (editingNeedId !== need.id) {
                            handleAccordionToggle(need.id)
                          }
                        }}
                        sx={{
                          color: '#1C3045',
                          border: '1px solid #1C3045',
                          borderRadius: '4px',
                          padding: '4px 12px',
                          fontSize: '0.8rem',
                          fontWeight: 500,
                          lineHeight: 1.5,
                          textTransform: 'none',
                          minWidth: '90px',
                          textAlign: 'center',
                          cursor: 'pointer',
                          display: 'inline-block',
                          '&:hover': {
                            backgroundColor: 'rgba(28, 48, 69, 0.04)',
                          },
                          '&:focus': {
                            backgroundColor: '#e8e8e8',
                          },
                        }}
                      >
                        View / Edit
                      </Button>
                      <FundingNeedMatchActions
                        token={token}
                        fundingNeed={need}
                        scanIds={
                          scanIds?.find(
                            (item) => item && item.fundingNeedId === need.id,
                          )?.scanIds
                        }
                        isAnnualSubscriber={isAnnualSubscriber}
                        hasPendingUpload={
                          rowHasPendingUpload ||
                          (editingNeedId === need.id && formPendingUpload)
                        }
                      />
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      width: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {org_sponsorship_letter && (
                          <Button
                            variant="contained"
                            disableRipple
                            disabled={!canWrite}
                            onClick={(e) => {
                              e.stopPropagation()
                              setSponsorshipNeed(need)
                              setShowSponsorshipModal(true)
                            }}
                            sx={{
                              // bgcolor: '#1C3045',
                              borderRadius: '4px',
                              padding: '6px 16px',
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              lineHeight: 1.75,
                              mr: 1,
                              textTransform: 'none',
                              minWidth: '100px',
                              textAlign: 'center',
                              cursor: 'pointer',
                              display: 'inline-block',
                            }}
                          >
                            Draft Sponsorship Letter
                          </Button>
                        )}
                        {org_can_set_preferences && (
                          <Button
                            disableRipple
                            disabled={!canWrite}
                            onClick={(e) => {
                              e.stopPropagation()
                              setPreferencesNeedId(need.id)
                            }}
                            sx={{
                              color: '#1C3045',
                              border: '1px solid #1C3045',
                              borderRadius: '4px',
                              padding: '6px 16px',
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              lineHeight: 1.75,
                              mr: 1,
                              textTransform: 'none',
                              minWidth: '100px',
                              textAlign: 'center',
                              cursor: 'pointer',
                              display: 'inline-block',
                              '&:hover': {
                                backgroundColor: 'rgba(28, 48, 69, 0.04)',
                              },
                              '&:focus': {
                                backgroundColor: '#e8e8e8',
                              },
                            }}
                          >
                            Match Preferences
                          </Button>
                        )}
                        <Button
                          ref={(el: HTMLButtonElement | null) => {
                            editButtonRefs.current[need.id] = el
                          }}
                          disableRipple
                          disabled={!canWrite && editingNeedId !== need.id}
                          onClick={(e) => {
                            e.stopPropagation()
                            if (editingNeedId === need.id) {
                              // show confirmation dialog if there are changes
                              if (formChanges.edit) {
                                setCancelConfirmation({
                                  type: 'edit',
                                  needId: need.id,
                                })
                              } else {
                                setEditingNeedId(null)
                                // This button keeps focus; keep its row open
                                // in case another row was expanded meanwhile.
                                setExpandedAccordion(need.id)
                              }
                            } else {
                              // Two things can be discarded by this click:
                              // whichever need is currently being edited, and
                              // this row's own staged quick-upload content.
                              // The open form is checked FIRST so the two can
                              // never both prompt - its dialog names the row's
                              // staged content too when both apply, rather
                              // than stacking a native confirm in front of it.
                              if (
                                editingNeedId !== null &&
                                (formChanges.edit || formPendingUpload)
                              ) {
                                setCancelConfirmation({
                                  type: 'edit',
                                  needId: editingNeedId,
                                  switchToNeedId: need.id,
                                  rowHasStagedUpload: rowHasPendingUpload,
                                })
                                return
                              }
                              // No form open, so only this row's staged
                              // quick-upload content is at stake.
                              if (
                                rowHasPendingUpload &&
                                !window.confirm(STAGED_UPLOAD_EDIT_CONFIRM)
                              ) {
                                return
                              }
                              setEditingNeedId(need.id)
                            }
                          }}
                          sx={{
                            color: '#1C3045',
                            border: '1px solid #1C3045',
                            borderRadius: '4px',
                            padding: '6px 16px',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            lineHeight: 1.75,
                            textTransform: 'none',
                            minWidth: '100px',
                            textAlign: 'center',
                            cursor: 'pointer',
                            display: 'inline-block',
                            '&:hover': {
                              backgroundColor: 'rgba(28, 48, 69, 0.04)',
                            },
                            '&:focus': {
                              backgroundColor: '#e8e8e8',
                            },
                          }}
                        >
                          {editingNeedId === need.id ? 'Cancel' : 'Edit'}
                        </Button>
                      </Box>
                      {/* Always mounted so screen readers announce the
                          text change; a region that mounts already holding
                          its message can go unannounced. */}
                      <Typography
                        role="status"
                        variant="caption"
                        sx={{ color: 'success.dark', display: 'block' }}
                      >
                        {editingNeedId !== need.id &&
                        justSaved?.needId === need.id
                          ? 'Changes saved'
                          : ''}
                      </Typography>
                      {editingNeedId !== need.id &&
                        (need.is_archived ? (
                          <Button
                            disableRipple
                            disabled={!canWrite}
                            onClick={() => {
                              updateFundingNeedArchiveStatus.mutate({
                                fundingNeedId: need.id,
                                token,
                              })
                            }}
                            sx={{
                              textTransform: 'none',
                              minWidth: '100px',
                              '&:focus': {
                                backgroundColor: '#e8e8e8',
                              },
                            }}
                          >
                            Unarchive
                          </Button>
                        ) : (
                          <Button
                            disableRipple
                            disabled={!canWrite}
                            onClick={() => {
                              if (
                                rowHasPendingUpload &&
                                !window.confirm(STAGED_UPLOAD_ARCHIVE_CONFIRM)
                              ) {
                                return
                              }
                              setShowArchiveWarning(need.id)
                            }}
                            sx={{
                              textTransform: 'none',
                              minWidth: '100px',
                              '&:focus': {
                                backgroundColor: '#e8e8e8',
                              },
                            }}
                          >
                            Archive
                          </Button>
                        ))}
                    </Box>
                    {sponsorshipNeed?.id === need.id && (
                      <GenerateSponsorshipLetter
                        isOpen={showSponsorshipModal}
                        onClose={() => setShowSponsorshipModal(false)}
                        token={token}
                        needId={need.id}
                        orgUuid={orgUuid}
                      />
                    )}
                    {editingNeedId === need.id ? (
                      <FundingNeedsForm
                        orgUuid={orgUuid}
                        token={token}
                        mode="edit"
                        fundingNeed={need}
                        onSuccess={() => {
                          saveSeqRef.current += 1
                          setJustSaved({
                            needId: need.id,
                            seq: saveSeqRef.current,
                          })
                          if (editingNeedIdRef.current !== need.id) return
                          setEditingNeedId(null)
                          setFormChanges({ edit: false })
                          // Keep this row open as its form closes: if another
                          // row was expanded meanwhile, this one would collapse
                          // and hide the Edit button and confirmation.
                          setExpandedAccordion(need.id)
                          returnFocusToEdit(need.id)
                        }}
                        onDeleted={() => {
                          setEditingNeedId(null)
                          setFormChanges({ edit: false })
                        }}
                        onCancel={() => {
                          setEditingNeedId(null)
                          setFormChanges({ edit: false })
                          setExpandedAccordion(need.id)
                          returnFocusToEdit(need.id)
                        }}
                        onFormStateChange={handleFormStateChange}
                        onPendingUploadChange={handlePendingUploadChange}
                        org_accepts_gambling_proceeds={
                          org_accepts_gambling_proceeds
                        }
                        org_accepts_international={org_accepts_international}
                        org_need_only_context={org_need_only_context}
                        org_region_ids={org_region_ids}
                        orgWebsite={orgWebsite}
                      />
                    ) : (
                      <>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mt: -4,
                          }}
                        >
                          <Box>
                            {need.amount && (
                              <>
                                <Typography
                                  variant="subtitle2"
                                  color="text.secondary"
                                >
                                  Amount Needed:
                                </Typography>
                                <Typography>${need.amount}</Typography>
                              </>
                            )}
                            {need.min_amount && (
                              <>
                                <Typography
                                  variant="subtitle2"
                                  color="text.secondary"
                                  mt={1}
                                >
                                  Minimum contribution amount:
                                </Typography>
                                <Typography>${need.min_amount}</Typography>
                              </>
                            )}
                          </Box>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            {`Locations${
                              need.uses_org_regions
                                ? ' (same as organisation)'
                                : ''
                            }:`}
                          </Typography>

                          <Typography>
                            {need?.region_ids && (regions?.length ?? 0) > 0
                              ? need.region_ids.length > 0
                                ? need.region_ids
                                    .map(
                                      (id: number) =>
                                        regionMap.get(id)?.name ||
                                        'Unknown Location',
                                    )
                                    .join(', ')
                                : 'Same as Organisation'
                              : 'Same as Organisation'}
                          </Typography>
                        </Box>

                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                            >
                              Accepts Gambling Proceeds
                            </Typography>
                            <AccessibleTooltip title="Proceeds from Lotteries, Pokies, and Casinos">
                              <span
                                role="img"
                                aria-label="Info: Proceeds from Lotteries, Pokies, and Casinos"
                                tabIndex={0}
                              >
                                <InfoOutlined
                                  sx={{
                                    fontSize: '18px',
                                    color: 'gray',
                                    ml: 0.5,
                                    mt: -1.5,
                                  }}
                                />
                              </span>
                            </AccessibleTooltip>
                          </Box>
                          <Typography
                            sx={{ whiteSpace: 'pre-wrap', pr: 10, pb: 1 }}
                          >
                            {(need.accepts_gambling_proceeds ??
                            org_accepts_gambling_proceeds)
                              ? 'Yes'
                              : 'No'}
                            {need.accepts_gambling_proceeds == null
                              ? ' (same as organisation)'
                              : ''}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                            >
                              Accepts International Funding
                            </Typography>
                            <AccessibleTooltip title="Funding Opportunities from Outside of New Zealand">
                              <span
                                role="img"
                                aria-label="Info: Funding Opportunities from Outside of New Zealand"
                                tabIndex={0}
                              >
                                <InfoOutlined
                                  sx={{
                                    fontSize: '18px',
                                    color: 'gray',
                                    ml: 0.5,
                                    mt: -1.5,
                                  }}
                                />
                              </span>
                            </AccessibleTooltip>
                          </Box>
                          <Typography
                            sx={{ whiteSpace: 'pre-wrap', pr: 10, pb: 1 }}
                          >
                            {(need.accepts_international ??
                            org_accepts_international)
                              ? 'Yes'
                              : 'No'}
                            {need.accepts_international == null
                              ? ' (same as organisation)'
                              : ''}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            Description:
                          </Typography>
                          <Typography
                            sx={{ whiteSpace: 'pre-wrap', pr: 10, pb: 2 }}
                          >
                            {need.description}
                          </Typography>
                        </Box>
                        <FundingNeedDocList
                          orgUuid={orgUuid}
                          token={token}
                          fundingNeedId={need.id}
                          orgWebsite={orgWebsite}
                        />
                        {!showUploadType ? (
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              width: '100%',
                              gap: 2,
                              mb: 1,
                            }}
                          >
                            <Button
                              disableRipple
                              disabled={!canWrite}
                              sx={uploadTypeToggleButtonSx}
                              variant="outlined"
                              onClick={() =>
                                handleShowUploadTypeToggle(need.id, 'files')
                              }
                            >
                              Upload Files
                            </Button>
                            <Button
                              disableRipple
                              disabled={!canWrite}
                              sx={uploadTypeToggleButtonSx}
                              variant="outlined"
                              onClick={() =>
                                handleShowUploadTypeToggle(need.id, 'websites')
                              }
                            >
                              Upload Website URLs
                            </Button>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              mb: 1,
                              display: 'flex',
                              justifyContent: 'center',
                            }}
                          >
                            <Tabs
                              value={uploadTabValue}
                              onChange={(_event, newValue) =>
                                handleUploadTabChange(need.id, newValue)
                              }
                              sx={{
                                mt: 2,
                                '& .MuiTabs-indicator': {
                                  display: 'none',
                                },
                                '& .MuiTab-root': {
                                  textTransform: 'none',
                                  fontSize: '16px',
                                  minHeight: '48px',
                                  borderRadius: '4px',
                                  border: '1px solid #1C3045',
                                  marginRight: '16px',
                                  minWidth: '120px',
                                  '&.Mui-selected': {
                                    backgroundColor: '#1C3045',
                                    color: '#fff',
                                  },
                                  '&:not(.Mui-selected)': {
                                    backgroundColor: '#fff',
                                    color: '#1C3045',
                                  },

                                  '&:hover': {
                                    backgroundColor: '#f5f5f5',
                                  },
                                  '&.Mui-selected:hover': {
                                    backgroundColor: '#1C3045',
                                  },
                                  '&:focus': {
                                    border: '2px solid #1C3045',
                                  },
                                },
                              }}
                            >
                              <Tab
                                disableRipple
                                disabled={rowHasPendingWebsites}
                                label="Upload Files"
                                aria-describedby={
                                  rowHasPendingUpload
                                    ? `funding-need-list-upload-pending-caption-${need.id}`
                                    : undefined
                                }
                              />
                              <Tab
                                disableRipple
                                disabled={rowHasPendingFiles}
                                label="Upload Website URLs"
                                aria-describedby={
                                  rowHasPendingUpload
                                    ? `funding-need-list-upload-pending-caption-${need.id}`
                                    : undefined
                                }
                              />
                            </Tabs>
                          </Box>
                        )}
                        {rowHasPendingUpload && (
                          <Box
                            sx={(theme) => ({
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mt: 1,
                              px: 1.5,
                              py: 0.75,
                              borderRadius: 1,
                              // Same derivation MUI uses for a standard warning Alert.
                              // warning.dark on white was only 3.8:1 and failed WCAG AA
                              // at caption size; this pairing is ~8.7:1 (#1465).
                              bgcolor: lighten(
                                theme.palette.warning.light,
                                0.9,
                              ),
                              color: darken(theme.palette.warning.light, 0.6),
                              border: `1px solid ${lighten(
                                theme.palette.warning.light,
                                0.7,
                              )}`,
                            })}
                          >
                            <WarningAmberOutlinedIcon
                              fontSize="small"
                              sx={{ flexShrink: 0 }}
                            />
                            <Typography
                              id={`funding-need-list-upload-pending-caption-${need.id}`}
                              role="status"
                              variant="caption"
                              sx={{ color: 'inherit' }}
                            >
                              {rowHasPendingFiles
                                ? "Your files aren't uploaded yet — upload or remove them. Switching to upload websites now would lose them."
                                : "Your website URLs aren't uploaded yet — upload or remove them. Switching to upload files now would lose them."}
                            </Typography>
                          </Box>
                        )}
                        {showUploadType === 'files' && (
                          <UploadFundingNeedFile
                            orgUuid={orgUuid}
                            token={token as string}
                            fundingNeedId={need.id}
                            onPendingChange={(pending) =>
                              setPendingFilesByNeedId((prev) => ({
                                ...prev,
                                [need.id]: pending,
                              }))
                            }
                          />
                        )}
                        {showUploadType === 'websites' && (
                          <UploadFundingNeedWebsiteUrls
                            orgUuid={orgUuid}
                            token={token as string}
                            fundingNeedId={need.id}
                            orgWebsite={orgWebsite}
                            onPendingChange={(pending) =>
                              setPendingWebsitesByNeedId((prev) => ({
                                ...prev,
                                [need.id]: pending,
                              }))
                            }
                          />
                        )}
                      </>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
              )
            })}
        </Box>
      )}
    </Box>
  )
}
